import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';

import { useModalManager } from '../../../../hooks/ModalManager.hook';
import { useAppDispatch } from '../../../../hooks/useAppDispatcher.hook';

import {
  createInternshipStatusControlThunk,
  deleteInternshipStatusControlThunk,
  getInternshipStatusControlThunk,
  updateInternshipStatusControlThunk,
} from '../../../../redux/ControlPanel/InternshipStatusControl/InternshipStatusControl.thunk';
import CardComponent from '../../../../components/Card.component';
import ButtonComponent from '../../../../components/Button.component';
import InputComponent from '../../../../components/Input/Input.component';
import SpinnerComponent from '../../../../components/Spinner.component';
import BadgeComponent from '../../../../components/Badge.component';
import ConfirmDuplicateModal from '../../../../components/Modal/Control/ConfirmDuplicateModal';
import ControlSectionContent from './ControlSectionContent.component';

import SearchIcon from '../../../../components/Icons/Search.icon';
import DragIcon from '../../../../components/Icons/Drag.icon';

import { StatusColor } from '../../../../constants/statusColors.constant';
import { ControlSectionTypes } from '../../../../redux/ControlPanel/ControlPanelList/ControlPanelList.type';
import {
  StyledControlListItem,
  StyledControlSectionContainer,
  StyledControlSectionHeader,
  StyledDragWrapper,
  StyledHeaderButtonsWrapper,
  StyledHeaderLabel,
  StyledHeaderTitle,
  StyledSearchIcon,
  StyledSearchWrapper,
  StyledSeparator,
  StyledStatusOrder,
} from './sharedStyledComponents';
import ControlCreateEditModal from '../../../../components/Modal/Control/ControlCreateEditModal';
import { InternshipStatus } from '../../../../redux/InternshipStatus/InternshipStatus.type';
import { getStatusColor } from '../../../../utils/colors';
import { useSelector } from 'react-redux';
import {
  getInternshipStatusControlList,
  getInternshipStatusControlListPagination,
  setInternshipStatusControlList,
} from '../../../../redux/ControlPanel/InternshipStatusControl/InternshipStatusControl.slice';

import { truncateText } from '../../../../utils/textTruncate';
import styled from 'styled-components';

const TruncatedBadgeWrapper = styled.div<{ $isTruncated?: boolean }>`
  display: inline-block;
  ${({ $isTruncated }) => $isTruncated && `
    cursor: help;
  `}
`;

const editable = true;
const viewable = true;
const deletable = true;
const creatable = true;
const sectionType = ControlSectionTypes.INTERNSHIP_STATUSES;

export const InternshipStatusesSectionComponent: FC = () => {
  const dispatch = useAppDispatch();
  const { openModal } = useModalManager();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const internshipStatuses = useSelector(getInternshipStatusControlList);
  const pagination = useSelector(getInternshipStatusControlListPagination);

  const dragItem = useRef<any>(null);
  const dragOverItem = useRef<any>(null);

  const fetchParams = useMemo(() => {
    return {
      search: searchQuery,
    };
  }, [searchQuery]);

  const INTERNSHIP_STATUS_MAX_LENGTH = 50;

  const createItemWithModalWrapper = async (
    fetchFunc: Function,
    updateFunc: Function,
    payload: any,
    updateFetchParams?: any,
    message?: string,
  ) => {
    dispatch(fetchFunc(payload)).then((res: any) => {
      if (!res.payload && !!res.error) {
        openModal(ConfirmDuplicateModal, {
          message: message ?? t(`Such a record already exists!`),
        });
      } else {
        dispatch(updateFunc(updateFetchParams));
      }
    });
  };

  const updateListAfterAction = () => {
    dispatch(getInternshipStatusControlThunk(fetchParams as any));
  };

  const createNewSectionItem = (newItem: InternshipStatus) => {
    const { name, color, textColor } = newItem;

    if (name) {
      createItemWithModalWrapper(
        createInternshipStatusControlThunk,
        getInternshipStatusControlThunk,
        {
          name,
          textColor,
          color: color ?? StatusColor.GREY,
        },
        fetchParams,
        t('Such a status already exists!'),
      );
    }
  };

  const editSectionItem = (sectionItem: InternshipStatus) => {
    const { name, color } = sectionItem;

    if (name && color) {
      createItemWithModalWrapper(
        updateInternshipStatusControlThunk,
        getInternshipStatusControlThunk,
        {
          ...sectionItem,
        },
        fetchParams,
        t('Such a status already exists in this group!'),
      );
    }
  };

  const deleteSectionItem = (sectionItem: InternshipStatus) => {
    dispatch(deleteInternshipStatusControlThunk(sectionItem.id)).then(() =>
      updateListAfterAction(),
    );
  };

  const updateInternshipStatusesOrder = async (
    statuses: InternshipStatus[],
  ) => {
    await Promise.all(
      statuses.map((changedStatus: InternshipStatus) => {
        const params = {
          id: changedStatus.id,
          name: changedStatus.name,
          color: changedStatus.color || StatusColor.GREY,
          textColor: changedStatus.textColor || StatusColor.GREY,
          order: changedStatus.order,
        };

        return dispatch(updateInternshipStatusControlThunk(params));
      }),
    );
  };

  const updateOrder = async () => {
    const changedStatuses = internshipStatuses.filter(
      (d: any) => d.oldOrder !== undefined && d.order !== d.oldOrder,
    );

    await updateInternshipStatusesOrder(changedStatuses as InternshipStatus[]);

    updateListAfterAction();
  };

  const handleDragStart = (e: React.DragEvent<HTMLLIElement>, status: any) => {
    dragItem.current = status;
  };

  const handleDragEnter = (e: React.DragEvent<HTMLLIElement>, status: any) => {
    dragOverItem.current = status;
    setDragOverIndex(status.id);
  };

  const handleDrop = (e: React.DragEvent<HTMLLIElement>) => {
    const data = [...internshipStatuses];
    const dragItemIndex = data.findIndex(d => d.id === dragItem?.current?.id);
    const dragOverItemIndex = data.findIndex(
      d => d.id === dragOverItem?.current?.id,
    );

    data.splice(dragItemIndex, 1);
    data.splice(dragOverItemIndex, 0, dragItem?.current);

    data
      .map((d: InternshipStatus, index: number) => ({
        ...d,
        order: index + 1,
        oldOrder: d.order,
      }))
      .forEach((changedItem: InternshipStatus) => {
        const changedItemIndex = data?.findIndex(d => d.id === changedItem.id);

        if (changedItemIndex != null) {
          data?.splice(changedItemIndex, 1, changedItem);
        }
      });

    dragItem.current = null;
    dragOverItem.current = null;

    updateItems(data);
    setDragOverIndex(null);
  };

  const handleSearch = async (value: string) => {
    await dispatch(
      getInternshipStatusControlThunk({
        q: value,
        page: 1,
        sort: 'order',
      }),
    );
  };

  const debounceSearchValue = useCallback(debounce(handleSearch, 300), []);

  const updateItems = (items: InternshipStatus[]) => {
    dispatch(setInternshipStatusControlList(items));
  };

  useEffect(() => {
    dispatch(
      getInternshipStatusControlThunk({
        sort: 'order',
      }),
    );
  }, []);

  if (!viewable) {
    return null;
  }

  const getBadgeProps = (status: InternshipStatus) => {
    const displayName = truncateText(status.name, INTERNSHIP_STATUS_MAX_LENGTH);
    const isTruncated = status.name.length > INTERNSHIP_STATUS_MAX_LENGTH;

    return {
      text: displayName,  // переименовали displayName → text
      isTruncated,
      color: getStatusColor(status.color),  // badgeColor → color
      textColor: getStatusColor(status.textColor),  // badgeTextColor → textColor
      title: isTruncated ? status.name : undefined
    };
  };

  return (
    <CardComponent>
      <StyledControlSectionContainer>
        <StyledControlSectionHeader>
          <StyledHeaderTitle>
            <StyledHeaderLabel>
              {t('Internship status management')}
            </StyledHeaderLabel>
          </StyledHeaderTitle>

          <StyledHeaderButtonsWrapper>
            {editable && (
              <ButtonComponent
                handleClick={updateOrder}
                textColor="#588fff"
                borderColor="#588fff"
              >
                {t('Save order')}
              </ButtonComponent>
            )}

            {creatable && (
              <ButtonComponent
                handleClick={() =>
                  openModal(ControlCreateEditModal, {
                    sectionType,
                    createNewSectionItem,
                    editable,
                    deletable,
                  })
                }
                textColor="#588fff"
                customColor="#ebf0ff"
              >
                {t('Create')}
              </ButtonComponent>
            )}
          </StyledHeaderButtonsWrapper>
        </StyledControlSectionHeader>

        <StyledSearchWrapper>
          <InputComponent
            value={searchQuery}
            onChange={(searchValue: string) => {
              setSearchQuery(searchValue);
              debounceSearchValue(searchValue);
            }}
            onEnter={handleSearch}
            placeholder={t(`Search ${sectionType}`)}
            right={
              <StyledSearchIcon>
                {isLoading ? <SpinnerComponent size="small" /> : <SearchIcon />}
              </StyledSearchIcon>
            }
          />
        </StyledSearchWrapper>

        {internshipStatuses.length && (
          <ControlSectionContent
            isLoadMode={pagination?.currentPage !== pagination?.lastPage}
            initialData={internshipStatuses}
            sectionType={sectionType}
            fetchData={getInternshipStatusControlThunk}
            fetchParams={{
              q: searchQuery,
              sort: `order`,
              page: pagination?.currentPage || 1,
            }}
            updateItems={updateItems}
            renderItem={item => {
              const badgeProps = getBadgeProps(item);

              return (
              <>
                {dragOverIndex === item.id && <StyledSeparator />}
                <StyledControlListItem
                  onClick={() =>
                    openModal(ControlCreateEditModal, {
                      sectionType,
                      sectionItem: item,
                      editSectionItem,
                      deleteSectionItem,
                      editable,
                      deletable,
                    })
                  }
                  onDragStart={e => handleDragStart(e, item)}
                  onDragEnter={e => handleDragEnter(e, item)}
                  onDragEnd={handleDrop}
                  key={item.id}
                  draggable={!searchQuery}
                >
                  <StyledDragWrapper>
                    {!searchQuery && (
                      <>
                        <DragIcon />
                        <StyledStatusOrder>{item.order}</StyledStatusOrder>
                      </>
                    )}
                    <TruncatedBadgeWrapper
                      $isTruncated={badgeProps.isTruncated}
                      title={badgeProps.title}
                    >
                      <BadgeComponent
                        appearance="default"
                        {...badgeProps}
                      />
                    </TruncatedBadgeWrapper>
                  </StyledDragWrapper>
                </StyledControlListItem>
              </>
            )}}
          />
        )}
      </StyledControlSectionContainer>
    </CardComponent>
  );
};
